/**
 * Application entry point.
 *
 * This file defines the "sitn_desktop" Closure namespace, which is be used as
 * the Closure entry point (see "closure_entry_point" in the "build.json"
 * file).
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import angular from 'angular';
import {isEventUsingCtrlKey} from 'ngeo/utils';
import gmfControllersAbstractDesktopController, {
  AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController';
import sitnBase from '../index';
import sitnAddressModule from 'sitn/address/module.js';
import sitnRasterstatisticsModule from 'sitn/rasterstatistics/module.js';
import sitnMistradistanceModule from 'sitn/mistradistance/module.js';
import sitnRedirectbuttonModule from 'sitn/redirectbutton/module.js';
import sitnExternalentryModule from 'sitn/externalentry/module.js';
import sitnFeedbackModule from 'sitn/feedback/module.js';
import sitnMiscFilters from 'sitn/misc/filters.js';
import sitnWastewaterFlowModule from 'sitn/wastewaterflow/module.js';
import gmfImportModule from 'gmf/import/module.js';
import ngeoMessagePopup from 'ngeo/message/Popup.js';
import ngeoMiscToolActivate from 'ngeo/misc/ToolActivate';
import panels from 'gmfapi/store/panels';
import htmlTemplate from './drawFeatureComponent.html';

/**
 * @private
 */
export class AbstractSITNController extends AbstractDesktopController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   */
  constructor($scope, $injector) {
    super($scope, $injector);

    // Allow angular-gettext-tools to collect the strings to translate
    // something like hidden attributes in Mapfiles
    /** @type {angular.gettext.gettextCatalog} */
    this.gettextCatalog = $injector.get('gettextCatalog');

    const $timeout = $injector.get('$timeout');

    /**
     * @type {gmf.theme.Manager}
     * @private
     */
    this.gmfThemeManager_ = $injector.has('gmfThemeManager') ? $injector.get('gmfThemeManager') : undefined;

    /**
     * @type {gmfx.User}
     * @private
     */
    this.gmfUser_ = $injector.has('gmfUser') ? $injector.get('gmfUser') : undefined;

    /**
     * The ngeo ToolActivate manager service.
     * @type {import("ngeo/misc/ToolActivateMgr.js").ToolActivateMgr}
     */
    this.ngeoToolActivateMgr = $injector.get('ngeoToolActivateMgr');

    /**
     * @type {boolean}
     */
    this.drawLidarprofilePanelActive = false;

    /**
     * @type {boolean}
     */
    this.lidarProfileFooterActive = true;

    // Make visible the footer
    panels.getActiveFooterPanel().subscribe({
      next: (panel) => {
        this.lidarProfileFooterActive = panel === 'lidar';
        $timeout(() => {}); // this triggered on DOM click, we call $timeout to force Angular diggest
      },
    });

    const drawLidarprofilePanelActive = new ngeoMiscToolActivate(this, 'drawLidarprofilePanelActive');
    this.ngeoToolActivateMgr.registerTool('mapTools', drawLidarprofilePanelActive, false);

    /**
     * @type {string}
     */
    this.sitnModalUrl;

    /**
     * @type {number}
     */
    this.sitnModalWidth;

    /**
     * @type {number}
     */
    this.sitnModalHeight;

    /**
     * @type {boolean}
     */
    this.sitnModalIsOpen = false;

    /**
     * @type {string}
     */
    this.sitnModalTitle;

    /**
     * @type {boolean}
     */
    this.showRasterStatbutton = false;

    /**
     * @type {boolean}
     */
    this.showNeophytebutton = false;

    /**
     * @type {boolean}
     */
    this.showMistraDistancebutton = false;

    /**
     * @type {boolean}
     */
    this.showWastewaterFlowbutton = false;

    /**
     * @type {Object}
     */
    this.panelData = {};

    /**
     * @type {Object}
     */
    this.panelType = {};

    /**
     * @type {angular.$locale} $locale Angular locale
     * @private
     */
    const $locale_ = $injector.get('$locale');
    // Set manually locale number format.
    $scope.$on('$localeChangeSuccess', () => {
      $locale_.NUMBER_FORMATS.DECIMAL_SEP = '.';
      $locale_.NUMBER_FORMATS.GROUP_SEP = "'";
    });

    /**
     * @type {boolean}
     */
    this.loaded = false;

    // open the component lidar panel when panel sould be active
    $scope.$watch(
      () => this.drawLidarprofilePanelActive,
      (newVal) => {
        if (newVal) {
          panels.openToolPanel('lidar');
        } else {
          panels.closeToolPanel();
        }
      },
    );

    // watch the loading state of the interface
    $scope.$watch(
      () => this.loading,
      () => {
        if (!this.loaded && !this.loading) {
          this.loaded = true;
        }
      },
    );

    // whatch themes change and activate tools when needed
    $scope.$watch(
      () => this.gmfThemeManager_.getThemeName(),
      (newTheme) => {
        if (newTheme === 'agriculture') {
          this.showRasterStatbutton = true;
        } else {
          this.showRasterStatbutton = false;
        }
        if (newTheme === 'cadastre_souterrain') {
          this.showWastewaterFlowbutton = true;
        } else {
          this.showWastewaterFlowbutton = false;
        }
        if (newTheme === 'neophytes') {
          this.showNeophytebutton = true;
        } else {
          this.showNeophytebutton = false;
        }
        if (newTheme === 'routes') {
          this.showMistraDistancebutton = true;
        } else {
          this.showMistraDistancebutton = false;
        }
      },
    );

    window['sitnExterns'] = {};

    // Bindings to sitnExterns
    window['gOpenWindow'] = this.sitnDisplayWindow.bind(this);
    window['sitnExterns']['openWindow'] = this.sitnDisplayWindow.bind(this);
    window['sitnExterns']['openPanel'] = this.sitnOpenPanel.bind(this);
    window['sitnExterns']['openTab'] = this.sitnOpenTab.bind(this);
    window['sitnExterns']['openUrl'] = this.sitnOpenUrl.bind(this);

    this.sitnHelpUrl = $injector.get('sitnHelpUrl');
    this.sitnAdresseHelpUrl = $injector.get('sitnAdresseHelpUrl');
    this.sitnNeophyteAnnounceUrl = $injector.get('sitnNeophyteAnnounceUrl');
    this.sitnWastewaterFlowUrl = $injector.get('sitnWastewaterFlowUrl');

    // The bindings here under should be added to Controlleriframe_api.js as well
    window['sitnExterns']['sitn_document_url'] = $injector.get('sitnDocumentUrl');
    window['sitnExterns']['sitn_lubis_url'] = $injector.get('sitnLubisUrl');
    window['sitnExterns']['sitn_orthophoto_url'] = $injector.get('sitnOrthophotoUrl');
    window['sitnExterns']['sitn_servitudes_url'] = $injector.get('sitnServitudesUrl');
    window['sitnExterns']['sitn_sau_list_url'] = $injector.get('sitnSauUrl');
    window['sitnExterns']['sitn_ouvrage_protection_url'] = $injector.get('sitnOuvrageProtectionUrl');
    window['sitnExterns']['sitn_ag_doc_list_url'] = $injector.get('sitnAgDocListUrl');
  }

  /**
   * @param {JQuery.Event} event keydown event.
   */
  onKeydown(event) {
    if (event && isEventUsingCtrlKey(event) && event.key === 'p') {
      this.printPanelActive = true;
      event.preventDefault();
    }
  }

  /**
   * @param {string} title Title.
   * @param {string} url Url.
   * @param {number} width Width.
   * @param {number} height Height.
   * Safe to call outside Angular context, $apply will take care of it
   * Cannot be called inside Angular context.
   */
  sitnDisplayWindow(title, url, width, height, x = 0, y = 0) {
    /* x and y are just here to ensure GeoGirafe compatibility */
    this.sitnDisplayInfoWindow(title, url, width, height);
    this.$scope.$apply();
  }

  /**
   * @param {string} title Title.
   * @param {string} url Url.
   * @param {number} width Width.
   * @param {number} height Height.
   * Callable from Angular context like inside a $watch
   */
  sitnDisplayInfoWindow(title, url, width, height) {
    this.sitnModalUrl = url;
    this.sitnModalWidth = width;
    this.sitnModalHeight = height;
    this.sitnModalTitle = title;
    this.sitnModalIsOpen = true;
  }

  /**
   * @param {string} moduleName The name of the target module.
   * @param {string} data Data to bind to module.
   */
  sitnOpenPanel(moduleName, data, type) {
    this.panelData[moduleName] = data;
    this.panelType[moduleName] = type;
    this[`${moduleName}PanelActive`] = true;
    this.$scope.$apply();
  }

  /**
   * @param {string} base_url url
   * @param {string} param_string params
   */
  sitnOpenTab(base_url, param_string) {
    let url;

    if (base_url in window['sitnExterns']) {
      url = window['sitnExterns'][base_url];
    } else {
      url = base_url;
    }
    if (param_string && param_string !== '') {
      url += '?' + param_string;
    }
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
    } else {
      alert("Merci d'autoriser l'ouverture de fenêtres supplémentaires");
    }
  }

  /**
   * @param {string} base_url url
   * @param {string} suffix params
   * @param {string} param_string params
   */
  sitnOpenUrl(base_url, suffix, param_string) {
    let url;

    if (base_url in window['sitnExterns']) {
      url = window['sitnExterns'][base_url];
    } else {
      url = base_url;
    }
    if (suffix) {
      url += '/' + suffix;
    }
    if (param_string) {
      url += '?' + param_string;
    }
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
    } else {
      alert("Merci d'autoriser l'ouverture de fenêtres supplémentaires");
    }
  }
}

AbstractSITNController.$inject = ['$scope', '$injector'];

/**
 * @hidden
 */
const sitnModule = angular.module('AppAbstractDesktop', [
  sitnBase.module.name,
  gmfControllersAbstractDesktopController.name,
  gmfImportModule.name,
  sitnAddressModule.name,
  sitnRasterstatisticsModule.name,
  sitnMistradistanceModule.name,
  sitnRedirectbuttonModule.name,
  sitnExternalentryModule.name,
  sitnFeedbackModule.name,
  sitnMiscFilters.name,
  sitnWastewaterFlowModule.name,
  ngeoMessagePopup.name,
]);

sitnModule.run(
  /**
   * @param {angular.ITemplateCacheService} $templateCache
   */
  [
    '$templateCache',
    ($templateCache) => {
      $templateCache.put('gmf/drawing/drawFeatureComponent', htmlTemplate);
    },
  ],
);

export default sitnModule;
